import { useEffect, useState } from 'react';

import {
  Anchor,
  Button,
  Container,
  Divider,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { defined } from '@teo/types';
import { useLocation, useNavigate } from 'react-router-dom';

import { GoogleAuthButton } from '../components/GoogleAuthButton';
import { RouteFallback } from '../components/RouteFallback';
import { useAuth } from '../contexts/AuthContext';
import { getSafePostLoginDestination } from '../lib/authRedirect';
import { storeAuthTokens } from '../lib/authSession';
import { readOf } from '../lib/readOf';
import { trpc } from '../lib/trpc';
import logger from '../utils/logger';
import { validateEmail, validatePassword } from '../utils/validation';

// Login form with email/password authentication
// Intent: Provide secure user authentication with password complexity requirements
// ensuring only printable ASCII characters and minimum length of 12 characters
export function LoginPage() {
  const { setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const postLoginDestination = getSafePostLoginDestination(location.search);

  /**
   * Where an environment with WorkOS configured sends the user instead of
   * rendering any of the form below. The backend answers "disabled" wherever
   * WorkOS credentials are absent — local development and CI — which is what
   * keeps the password form, and the spec suite that drives it, working
   * unchanged while UAT runs the hosted flow.
   *
   * This branch is not a permanent fork. It is the seam the legacy form is
   * eventually deleted along, once no environment answers "disabled".
   */
  const workosLogin = useQuery({
    ...trpc.auth.workosLoginUrl.queryOptions({ returnPath: postLoginDestination }),
    // No retry, unlike every other query in the app. This one decides whether a
    // login page appears at all, so retrying with backoff against a backend
    // that is not answering leaves a spinner where the form should be.
    retry: false,
  });
  const workosLoginRead = readOf(workosLogin);
  const hostedSignInUrl = workosLogin.data?.status === 'enabled' ? workosLogin.data.url : undefined;

  useEffect(() => {
    if (hostedSignInUrl) {
      // Assigning rather than navigating: AuthKit is a different origin, so
      // this has to leave the SPA entirely.
      window.location.assign(hostedSignInUrl);
    }
  }, [hostedSignInUrl]);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    initialErrors: {},
    validateInputOnBlur: false,
    validateInputOnChange: false,
    validate: {
      email: (value) => {
        const result = validateEmail(value);
        return result.success ? null : result.error;
      },
      password: (value) => {
        const result = validatePassword(value);
        return result.success ? null : result.error;
      },
    },
  });

  const loginMutation = useMutation(
    trpc.auth.login.mutationOptions({
      onSuccess: (data) => {
        logger.log(`Login Success ${JSON.stringify(data)}`);
        storeAuthTokens(
          {
            token: data.token,
            ...defined({ refreshToken: data.refreshToken }),
          },
          'login-success',
        );
        setUser(data.user);
        setIsLoading(false);
        void navigate(postLoginDestination, { replace: true });
      },
      onError: (error) => {
        if (error instanceof Error) {
          if (
            error.message.includes('Invalid email or password') ||
            error.message.includes('UNAUTHORIZED')
          ) {
            form.setFieldError(
              'password',
              'Invalid email or password. Please check your credentials.',
            );
          } else {
            form.setFieldError('password', 'Login failed. Please try again.');
          }
        }
        setIsLoading(false);
      },
    }),
  );

  const handleSubmit = async () => {
    const values = form.values;
    // Validate all fields and show errors
    const validation = form.validate();
    if (validation.hasErrors) return;

    setIsLoading(true);
    loginMutation.mutate(values);
  };

  // Nothing of ours should flash before the redirect, and a half-rendered
  // password form is exactly the thing that makes a hosted flow look broken.
  //
  // Only while the answer is genuinely in flight, though. If asking fails we
  // render the form: it is the branch that needs nothing from the backend in
  // order to draw, so the user gets a login page and a real error on submit
  // instead of a spinner that never resolves. The built-bundle smoke test
  // serves the frontend with no backend at all and asserts exactly this.
  if ((workosLogin.isPending && workosLoginRead.kind !== 'failed') || hostedSignInUrl) {
    return <RouteFallback />;
  }

  return (
    <Container size="xs" py="xl">
      <Paper px="xl" py="sm" withBorder>
        <img
          src="/logos/logo-dark.png"
          alt="TEO"
          style={{
            width: '50%',
            maxWidth: '100px',
            display: 'block',
            margin: '0 auto',
          }}
        />

        <Title order={2} ta="center" mb="md">
          Sign In
        </Title>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              required
              type="email"
              role="textbox"
              aria-label="Email"
              label="Email"
              placeholder="Enter your email address"
              error={form.errors.email}
              data-testid="login-email-input"
              {...form.getInputProps('email')}
            />

            <PasswordInput
              required
              role="textbox"
              aria-label="Password"
              label="Password"
              placeholder="Enter your password (min 12 characters)"
              error={form.errors.password}
              visibilityToggleButtonProps={{
                role: 'button',
                'aria-label': 'Toggle password visibility',
              }}
              data-testid="login-password-input"
              {...form.getInputProps('password')}
            />

            <Button
              type="submit"
              loading={isLoading}
              fullWidth
              variant="outline"
              data-testid="login-submit-button"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Divider label="Or" labelPosition="center" my="md" />

            <div style={{ marginTop: 'calc(-1 * var(--mantine-spacing-md))' }}>
              <GoogleAuthButton
                mode="login"
                fullWidth
                onSuccess={async () => {
                  await navigate(postLoginDestination, { replace: true });
                }}
                onError={(error) => {
                  logger.error({ error }, 'Google login error:');
                  form.setFieldError(
                    'password',
                    'Google login failed. Please try again or use email login.',
                  );
                }}
              />
            </div>
          </Stack>
        </form>
        <Text ta="center" size="sm" pt="xl">
          Don't have an account? <Anchor href="/signup">Sign up</Anchor>
        </Text>
      </Paper>
    </Container>
  );
}
